import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { ISelectBoxOption, SelectBox } from '../../../../../../components/SelectBox';
import { styleSheet } from '../styles';

interface FieldTypeSelectorProps {
	field: Api.IFormField<string>;
	questionIndex: number;
	isEdit: boolean;
	hasBeenSaved: boolean;
	disabledInputs: boolean;
	formFieldOptions: {
		title: string;
		value: Api.FormFieldType;
	}[];
	onRenderTriggerTitle?: (selectedOption: ISelectBoxOption<Api.FormFieldType>) => React.ReactNode;
	onSelectionChanged: (selectedType: ISelectBoxOption<Api.FormFieldType>, questionIndex: number) => void;
}

export const FieldTypeSelector = React.memo<FieldTypeSelectorProps>(
	({
		field,
		questionIndex,
		isEdit,
		hasBeenSaved,
		disabledInputs,
		formFieldOptions,
		onRenderTriggerTitle,
		onSelectionChanged,
	}) => {
		// Validate required props early to fail fast
		if (!field) {
			throw new Error('FieldTypeSelector: field prop is required');
		}
		if (!formFieldOptions) {
			throw new Error('FieldTypeSelector: formFieldOptions prop is required');
		}

		// Memoize expensive calculations
		const isDisabled = React.useMemo(
			() => (isEdit && hasBeenSaved) || disabledInputs,
			[isEdit, hasBeenSaved, disabledInputs]
		);

		const selectedOption = React.useMemo(
			() => formFieldOptions.find(x => x.value === field.fieldType),
			[formFieldOptions, field.fieldType]
		);

		// Memoize trigger className
		const triggerClassName = React.useMemo(
			() => css(styleSheet.dropdownTrigger, isDisabled ? styleSheet.dropdownTriggerDisabled : undefined),
			[isDisabled]
		);

		// Memoize menu className
		const menuClassName = React.useMemo(() => `${css(styleSheet.dropdownMenu)}`, []);

		// Memoize selection handler
		const handleSelectionChanged = React.useCallback(
			(selectedType: ISelectBoxOption<Api.FormFieldType>) => onSelectionChanged(selectedType, questionIndex),
			[onSelectionChanged, questionIndex]
		);

		return (
			<SelectBox
				disabled={isDisabled}
				menuClassName={menuClassName}
				onRenderSelectedOptionTitle={onRenderTriggerTitle}
				onSelectionChanged={handleSelectionChanged}
				options={formFieldOptions}
				selectedOption={selectedOption}
				triggerClassName={triggerClassName}
			/>
		);
	}
);

FieldTypeSelector.displayName = 'FieldTypeSelector';
